import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import { installStateLabel } from '../../api/remoteConnectRustdesk'
import {
  saveRustdeskCompanyServers,
  saveRustdeskInstallMode,
  saveRustdeskServer,
  saveRustdeskWhitelist,
  type RemoteConnectShell,
  type RustdeskDeviceState,
  type RustdeskOrgSection,
  type RustdeskServer,
  type RustdeskServerForm,
} from '../../api/settingsRemoteConnect'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { esc } from '../users/shared'
import '../../ui/SettingsParts.css'
import '../rustdesk/RustdeskSettingsPage.css'
import { RustdeskSecurityNote } from './RustdeskSecurityNote'
import './RustdeskOrgPanels.css'

/**
 * Settings -> Company -> RUSTDESK, partea pe ORGANIZATIE (firma aleasa in
 * meniul de firma). Serverul se scrie pe api, in
 * `App\Settings\RemoteConnect\RustdeskOrg`; aici se deseneaza si se trimite.
 *
 * PANA PE 6 OCTOMBRIE 2026 STATEA SUB REMOTE CONNECT, sub bifele ScreenConnect,
 * cu titlul intr-un `<h4>` fara clasa. `od_console.css` pune pe un asemenea
 * titlu, si pe TOT ce e in el, litera serif de 22px - deci si pe
 * `<i class="fas">`, care isi pierdea fontul de iconite si iesea o casuta cu
 * cod. Acum titlul e `<h3>`, ca pe celelalte ecrane de setari, iar iconita sta
 * pe buton.
 *
 * Bifele si butoanele radio sunt `label.od-inv-check` (ui/SettingsParts.css):
 * cele din `.radio > label` ale Bootstrap-ului stau `position: absolute` cu
 * `margin-left: -20px`, iar consola trage eticheta inapoi la stanga - cerculetul
 * cadea peste primele litere ("start", "n demand").
 *
 *  - Install RustDesk: At start (implicit) / On demand / ca furnizorul;
 *  - Allowed technician IPs: pe firma, cu mostenire de la furnizor;
 *  - Servers for this organization: alegerea ordonata a firmei;
 *  - starea instalarii pe aparatele firmei, din raportul agentului;
 *  - "How RustDesk is secured": pagina pentru clienti, intr-o fereastra.
 *
 * Lista serverelor FURNIZORULUI (`RustdeskProviderServers`) sta in fila
 * "RustDesk servers" din acelasi meniu, langa setarile furnizorului.
 */

type Ready = Extract<RustdeskOrgSection, { ready: true }>

type Saved = RemoteConnectShell & { saved: boolean }

type Save = (promise: Promise<Saved>, title: string, done?: () => void) => Promise<void>

function failed(error: unknown): string {
  return error instanceof ApiError && error.message ? error.message : 'Something went wrong.'
}

/** Scrierea comuna: raspunsul e ecranul intreg, plus un toast cu titlul panoului. */
function saver(onSaved: (next: Saved) => void): Save {
  return (promise, title, done) =>
    promise
      .then((next) => {
        onSaved(next)
        toast('Settings saved', title)
        done?.()
      })
      .catch((error: unknown) => {
        toast(failed(error), title, 'error')
      })
}

const EMPTY_SERVER: RustdeskServerForm = {
  label: '',
  hbbs_host: '',
  hbbs_port: '21116',
  hbbr_host: '',
  hbbr_port: '21117',
  server_key: '',
  active: true,
}

function formOf(server: RustdeskServer): RustdeskServerForm {
  return {
    id: server.id,
    label: server.label,
    hbbs_host: server.hbbsHost,
    hbbs_port: String(server.hbbsPort),
    hbbr_host: server.hbbrHost,
    hbbr_port: String(server.hbbrPort),
    server_key: server.serverKey,
    active: server.active,
  }
}

function serverName(server: RustdeskServer): string {
  return server.label !== '' ? server.label : `${server.hbbsHost}:${server.hbbsPort}`
}

/** Randul cu butonul de salvare, la dreapta, ca pe celelalte ecrane de setari. */
function SaveRow({ disabled, label = 'Save' }: { disabled?: boolean; label?: string }) {
  return (
    <div className="rd-org-actions">
      <button className="btn btn-success" type="submit" disabled={disabled}>
        {label}
      </button>
    </div>
  )
}

export function RustdeskOrgPanels({
  section,
  canWrite,
  onSaved,
}: {
  section: RustdeskOrgSection | undefined
  canWrite: boolean
  onSaved(next: Saved): void
}) {
  const [securityOpen, setSecurityOpen] = useState(false)

  return (
    <div className="rd-org" id="rustdesk-organization">
      <div className="rd-org-head">
        <h3>RustDesk for this organization</h3>
        <button
          type="button"
          className="btn btn-default btn-sm"
          id="rustdesk-security-open"
          onClick={() => setSecurityOpen(true)}
        >
          <i className="fas fa-shield-alt" aria-hidden="true"></i> How RustDesk is secured
        </button>
      </div>
      <p className="rd-org-lead">
        How RustDesk is installed and reached on the devices of the organization selected in the company menu.
      </p>

      {section && section.ready ? (
        <Panels section={section} canWrite={canWrite} save={saver(onSaved)} />
      ) : (
        <div className="alert alert-warning" role="alert">
          RustDesk organization settings are not available yet: the database update has not run on this server.
        </div>
      )}

      <Modal
        id="rustdesk-security"
        open={securityOpen}
        onClose={() => setSecurityOpen(false)}
        title="How RustDesk is secured at OptimumDesk"
        large
        portal
      >
        <RustdeskSecurityNote />
      </Modal>
    </div>
  )
}

function Panels({ section, canWrite, save }: { section: Ready; canWrite: boolean; save: Save }) {
  return (
    <>
      <div className="row">
        <div className="col-md-6">
          <InstallModePanel section={section} canWrite={canWrite} save={save} />
          <CompanyServersPanel section={section} canWrite={canWrite} save={save} />
        </div>
        <div className="col-md-6">
          <WhitelistPanel section={section} canWrite={canWrite} save={save} />
        </div>
      </div>
      <DevicesPanel devices={section.devices} />
    </>
  )
}

function InstallModePanel({ section, canWrite, save }: { section: Ready; canWrite: boolean; save: Save }) {
  const current = section.installModeInherited ? 'inherit' : section.installMode
  const [mode, setMode] = useState<'start' | 'demand' | 'inherit'>(current)
  useEffect(() => setMode(current), [current])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    void save(saveRustdeskInstallMode(mode), 'Install RustDesk')
  }

  return (
    <div className="panel panel-default">
      <div className="panel-heading">
        <h3 className="panel-title">Install RustDesk</h3>
      </div>
      <form className="panel-body" id="rustdesk-install-mode" onSubmit={submit}>
        {(
          [
            ['start', 'At start', 'RustDesk is installed as soon as the agent starts, so a connection never waits for a download.'],
            ['demand', 'On demand', 'RustDesk is installed only when someone presses Install RustDesk or Connect on a device. Switching to this does not remove RustDesk from devices that already have it.'],
            ['inherit', 'Same as the service provider', 'Follow the service provider default (On demand, unless changed).'],
          ] as const
        ).map(([value, label, hint]) => (
          <div className="rd-choice" key={value}>
            <label className="od-inv-check" htmlFor={`rdMode-${value}`}>
              <input
                type="radio"
                id={`rdMode-${value}`}
                name="install_mode"
                value={value}
                checked={mode === value}
                disabled={!canWrite}
                onChange={() => setMode(value)}
              />
              <strong>{label}</strong>
            </label>
            <div className="rd-choice-hint">{hint}</div>
          </div>
        ))}
        <p className="rd-org-note">
          In effect now: <strong>{section.installMode === 'demand' ? 'On demand' : 'At start'}</strong>
          {section.installModeInherited ? ' (from the service provider)' : ''}
        </p>
        {canWrite ? <SaveRow /> : null}
      </form>
    </div>
  )
}

function WhitelistPanel({ section, canWrite, save }: { section: Ready; canWrite: boolean; save: Save }) {
  const [inherit, setInherit] = useState(section.whitelistInherited)
  const [text, setText] = useState(section.whitelistInherited ? '' : section.whitelist.join('\n'))
  useEffect(() => {
    setInherit(section.whitelistInherited)
    setText(section.whitelistInherited ? '' : section.whitelist.join('\n'))
  }, [section.whitelistInherited, section.whitelist])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    void save(saveRustdeskWhitelist({ scope: 'company', inherit, whitelist: text }), 'Allowed technician IPs')
  }

  return (
    <div className="panel panel-default">
      <div className="panel-heading">
        <h3 className="panel-title">Allowed technician IPs</h3>
      </div>
      <form className="panel-body" id="rustdesk-whitelist" onSubmit={submit}>
        <p className="rd-org-note">
          When this list is filled in, RustDesk on this organization's devices accepts connections <strong>only</strong>{' '}
          from these addresses, also when the connection goes through the relay. Use the fixed public (VPN or NAT) addresses
          your technicians connect from. One IP address or CIDR range per line, for example <code>203.0.113.7</code> or{' '}
          <code>198.51.100.0/24</code>. Empty means no restriction by address.
        </p>
        <div className="rd-choice">
          <label className="od-inv-check" htmlFor="rdWlInherit">
            <input
              type="checkbox"
              id="rdWlInherit"
              checked={inherit}
              disabled={!canWrite}
              onChange={(e) => setInherit(e.target.checked)}
            />
            Same as the service provider
            {section.providerWhitelist.length > 0
              ? ` (${section.providerWhitelist.join(', ')})`
              : ' (no restriction)'}
          </label>
        </div>
        <textarea
          className="form-control"
          id="rdWlText"
          aria-label="Allowed technician IPs"
          rows={4}
          value={inherit ? section.providerWhitelist.join('\n') : text}
          disabled={!canWrite || inherit}
          onChange={(e) => setText(e.target.value)}
          placeholder="203.0.113.7&#10;198.51.100.0/24"
        />
        {canWrite ? <SaveRow /> : null}
      </form>
    </div>
  )
}

function CompanyServersPanel({ section, canWrite, save }: { section: Ready; canWrite: boolean; save: Save }) {
  const active = section.servers.filter((s) => s.active)
  const [useDefault, setUseDefault] = useState(section.companyServerIds.length === 0)
  const [chosen, setChosen] = useState<number[]>(section.companyServerIds)
  useEffect(() => {
    setUseDefault(section.companyServerIds.length === 0)
    setChosen(section.companyServerIds)
  }, [section.companyServerIds])

  const byId = new Map(section.servers.map((s) => [s.id, s]))
  const others = active.filter((s) => !chosen.includes(s.id))

  const move = (index: number, delta: number) => {
    const next = [...chosen]
    const target = index + delta
    if (target < 0 || target >= next.length) return
    ;[next[index], next[target]] = [next[target], next[index]]
    setChosen(next)
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    void save(saveRustdeskCompanyServers(useDefault ? [] : chosen), 'RustDesk servers')
  }

  return (
    <div className="panel panel-default">
      <div className="panel-heading">
        <h3 className="panel-title">Servers for this organization</h3>
      </div>
      <form className="panel-body" id="rustdesk-company-servers" onSubmit={submit}>
        <p className="rd-org-note">
          RustDesk on a device uses one server at a time. The agent picks the first server in this list that answers and
          stays on it while it answers; if it stops answering, the agent moves to the next one. The order is priority and
          fallback, not load balancing. The server each device is on is shown in the list of devices below.
        </p>
        <div className="rd-choice">
          <label className="od-inv-check" htmlFor="rdSrvDefault">
            <input
              type="checkbox"
              id="rdSrvDefault"
              checked={useDefault}
              disabled={!canWrite}
              onChange={(e) => setUseDefault(e.target.checked)}
            />
            Use all active service provider servers
          </label>
        </div>
        {!useDefault && (
          <table className="table table-condensed" id="rdCompanyServers">
            <tbody>
              {chosen.map((id, i) => {
                const server = byId.get(id)
                return (
                  <tr key={id}>
                    <td>
                      {i + 1}. {server ? serverName(server) : `#${id}`}
                      {server && !server.active ? <span className="label label-default"> disabled</span> : null}
                    </td>
                    <td className="align-right" style={{ whiteSpace: 'nowrap' }}>
                      {canWrite && (
                        <>
                          <button type="button" className="btn btn-default btn-xs" aria-label="Move up" onClick={() => move(i, -1)}>
                            <i className="fas fa-arrow-up" aria-hidden="true"></i>
                          </button>{' '}
                          <button type="button" className="btn btn-default btn-xs" aria-label="Move down" onClick={() => move(i, 1)}>
                            <i className="fas fa-arrow-down" aria-hidden="true"></i>
                          </button>{' '}
                          <button
                            type="button"
                            className="btn btn-default btn-xs"
                            aria-label="Remove"
                            onClick={() => setChosen(chosen.filter((x) => x !== id))}
                          >
                            <i className="fas fa-times" aria-hidden="true"></i>
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                )
              })}
              {canWrite &&
                others.map((server) => (
                  <tr key={`add-${server.id}`} className="text-muted">
                    <td>{serverName(server)}</td>
                    <td className="align-right">
                      <button type="button" className="btn btn-default btn-xs" onClick={() => setChosen([...chosen, server.id])}>
                        <i className="fas fa-plus" aria-hidden="true"></i> Add
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
        {canWrite ? <SaveRow disabled={!useDefault && chosen.length === 0} /> : null}
      </form>
    </div>
  )
}

/**
 * Serverele RustDesk ale FURNIZORULUI (`it_remote_connect_servers`) si lista
 * implicita de IP-uri. Sta in fila "RustDesk servers" din meniul de firma, langa
 * setarile furnizorului (`RustdeskProviderSettings`); datele vin tot din
 * ecranul Remote Connect al api-ului, neschimbat.
 */
export function RustdeskProviderServers({
  section,
  canWrite,
  onSaved,
}: {
  section: RustdeskOrgSection | undefined
  canWrite: boolean
  onSaved(next: Saved): void
}) {
  if (!section || !section.ready) return null
  return <ProviderServersPanel section={section} canWrite={canWrite} save={saver(onSaved)} />
}

function ProviderServersPanel({ section, canWrite, save }: { section: Ready; canWrite: boolean; save: Save }) {
  const [editing, setEditing] = useState<RustdeskServerForm | null>(null)
  const [providerList, setProviderList] = useState(section.providerWhitelist.join('\n'))
  useEffect(() => setProviderList(section.providerWhitelist.join('\n')), [section.providerWhitelist])

  return (
    <div className="rd-card" id="rustdesk-provider-servers">
      <div className="rd-org-cardhead">
        <h4>Servers</h4>
        {canWrite && (
          <button type="button" className="btn btn-default btn-sm" onClick={() => setEditing({ ...EMPTY_SERVER })}>
            <i className="fas fa-plus" aria-hidden="true"></i> Add server
          </button>
        )}
      </div>
      <p className="rd-note" style={{ marginBottom: 12 }}>
        Every organization uses all active servers, in this order, unless it picks its own under RustDesk.
      </p>
      <table className="table table-condensed" id="rdProviderServers">
        <thead>
          <tr>
            <th>Name</th>
            <th>ID server</th>
            <th>Relay</th>
            <th>Key</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {section.servers.map((server) => (
            <tr key={server.id} className={server.active ? '' : 'text-muted'}>
              <td>
                {server.label || '-'}
                {server.fromSettings ? <span className="text-muted small"> (default server, below)</span> : null}
                {!server.active ? <span className="label label-default"> disabled</span> : null}
              </td>
              <td>
                {server.hbbsHost}:{server.hbbsPort}
              </td>
              <td>
                {server.hbbrHost || server.hbbsHost}:{server.hbbrPort}
              </td>
              <td title={server.serverKey}>
                <code>{server.serverKey ? `${server.serverKey.slice(0, 6)}…` : 'missing'}</code>
              </td>
              <td className="align-right">
                {canWrite && (
                  <button type="button" className="btn btn-default btn-xs" onClick={() => setEditing(formOf(server))}>
                    Edit
                  </button>
                )}
              </td>
            </tr>
          ))}
          {section.servers.length === 0 && (
            <tr>
              <td colSpan={5} className="text-muted">
                No servers yet. The default server below is the first one.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Nota pentru administrator, nu pentru client: ce trebuie sa faca
          serverul ca setarile de pe statii sa insemne ceva. */}
      <div className="alert alert-info small rd-org-requirements">
        <strong>Server requirements.</strong> Run <code>hbbs</code> with its key (the default; the key shown here is its{' '}
        <code>id_ed25519.pub</code>). Start <code>hbbr</code> with <code>-k _</code> — without it the relay is open to
        anyone. Optionally set <code>ALWAYS_USE_RELAY=Y</code> on <code>hbbs</code> so all traffic goes through our relay.
      </div>

      <form
        id="rustdesk-provider-whitelist"
        onSubmit={(e) => {
          e.preventDefault()
          void save(saveRustdeskWhitelist({ scope: 'provider', whitelist: providerList }), 'Allowed technician IPs')
        }}
      >
        <label className="control-label" htmlFor="rdWlProvider">
          Allowed technician IPs, service provider default
        </label>
        <textarea
          className="form-control"
          id="rdWlProvider"
          rows={3}
          value={providerList}
          disabled={!canWrite}
          onChange={(e) => setProviderList(e.target.value)}
        />
        {canWrite ? <SaveRow label="Save default" /> : null}
      </form>

      <Modal
        id="rustdesk-server"
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing?.id ? 'Edit RustDesk server' : 'Add RustDesk server'}
        portal
      >
        {editing && (
          <ServerForm
            form={editing}
            onChange={setEditing}
            onSubmit={() => void save(saveRustdeskServer(editing), 'RustDesk servers', () => setEditing(null))}
          />
        )}
      </Modal>
    </div>
  )
}

function ServerForm({
  form,
  onChange,
  onSubmit,
}: {
  form: RustdeskServerForm
  onChange(next: RustdeskServerForm): void
  onSubmit(): void
}) {
  const field = (name: keyof RustdeskServerForm, label: string, hint?: string) => (
    <div className="form-group">
      <label htmlFor={`rdSrv-${name}`}>{label}</label>
      <input
        className="form-control"
        id={`rdSrv-${name}`}
        value={String(form[name] ?? '')}
        onChange={(e) => onChange({ ...form, [name]: e.target.value })}
      />
      {hint ? <span className="help-block small">{hint}</span> : null}
    </div>
  )

  return (
    <form
      id="rustdesk-server-form"
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit()
      }}
    >
      {field('label', 'Name')}
      {field('hbbs_host', 'ID server (hbbs) host', 'Host name or IPv4 address, without port.')}
      {field('hbbs_port', 'ID server port')}
      {field('hbbr_host', 'Relay server (hbbr) host', 'Empty = the same host as the ID server.')}
      {field('hbbr_port', 'Relay server port')}
      {field('server_key', 'Server key', "The 44-character public key of the server (id_ed25519.pub).")}
      <div className="rd-choice">
        <label className="od-inv-check" htmlFor="rdSrv-active">
          <input
            type="checkbox"
            id="rdSrv-active"
            checked={form.active}
            onChange={(e) => onChange({ ...form, active: e.target.checked })}
          />
          Active
        </label>
      </div>
      <SaveRow label="Save server" />
    </form>
  )
}

function stateClass(state: string): string {
  switch (state) {
    case 'installed':
      return 'label label-success'
    case 'failed':
      return 'label label-danger'
    case 'downloading':
    case 'installing':
      return 'label label-info'
    default:
      return 'label label-default'
  }
}

/** Id-ul tabelului: si `id`-ul `<table>`, si cheia asezarii tinute in baza. */
const DEVICES_TABLE = 'rdDeviceStates'

type DeviceRow = RustdeskDeviceState & Record<string, unknown>

/** Celula Install: eticheta colorata, cererea si motivul caderii pe randul doi. */
function installHtml(d: RustdeskDeviceState): string {
  let html = `<span class="${stateClass(d.installState)}">${esc(installStateLabel(d.installState))}</span>`
  if (d.requestedAt !== '' && d.installState !== 'installed') {
    html += ` <span class="text-muted small">requested ${esc(d.requestedAt)}</span>`
  }
  if (d.installError !== '') html += `<div class="text-danger small">${esc(d.installError)}</div>`
  return html
}

function devicesColumns(): TableColumn[] {
  const row = (r: Record<string, unknown>) => r as DeviceRow
  return [
    {
      index: 0,
      title: 'Device',
      orderable: true,
      visible: true,
      /* Fisa aparatului, in fila noua: pagina de setari ramane deschisa. */
      html: (r) =>
        `<a href="/app/inventoree/device/${Number(row(r).deviceId)}" target="_blank" rel="noopener">${esc(row(r).name || `#${row(r).deviceId}`)}</a>`,
    },
    { index: 1, title: 'RustDesk ID', orderable: true, visible: true, html: (r) => esc(row(r).rustdeskId || '-') },
    { index: 2, title: 'Install', orderable: true, visible: true, html: (r) => installHtml(row(r)) },
    { index: 3, title: 'Server in use', orderable: true, visible: true, html: (r) => esc(row(r).activeServer || '-') },
    {
      index: 4,
      title: 'Settings restored',
      orderable: true,
      visible: true,
      html: (r) => {
        const d = row(r)
        return d.tamperCount > 0
          ? `<span class="text-warning" title="${esc(d.lastTamperKeys)}">${d.tamperCount}x, last ${esc(d.lastTamperAt)}</span>`
          : '-'
      },
    },
  ]
}

/** Dupa ce se sorteaza, pe indicele coloanei. */
const DEVICE_SORT: Array<(d: RustdeskDeviceState) => string | number> = [
  (d) => d.name.toLowerCase(),
  (d) => d.rustdeskId,
  (d) => installStateLabel(d.installState).toLowerCase(),
  (d) => d.activeServer.toLowerCase(),
  (d) => d.tamperCount,
]

/** Ce se cauta: tot ce scrie in rand. */
function deviceText(d: RustdeskDeviceState): string {
  return [d.name, d.rustdeskId, installStateLabel(d.installState), d.requestedAt, d.installError, d.activeServer, d.lastTamperAt]
    .join(' ')
    .toLowerCase()
}

/**
 * Starea RustDesk pe aparatele firmei, cu tabelul listelor din Settings
 * (`ui/DataTable`): cautarea in antetul paginii, sortarea pe capete, numarul de
 * randuri si paginile in subsol, asezarea tinuta in baza (`table-state`).
 *
 * Aparatele vin INTREGI in ecranul Remote Connect (`section.devices`), deci
 * cautarea, sortarea si pagina se fac aici, pe client - ca la Fleet din
 * Agent releases. Capatul ramane neschimbat.
 */
function DevicesPanel({ devices }: { devices: RustdeskDeviceState[] }) {
  const [state, setState] = useTableState(DEVICES_TABLE, {
    start: 0,
    length: 25,
    order: { column: 0, dir: 'asc' },
    search: '',
  })
  const columns = useMemo(() => devicesColumns(), [])

  const filtered = useMemo(() => {
    const q = state.search.trim().toLowerCase()
    return q === '' ? devices : devices.filter((d) => deviceText(d).includes(q))
  }, [devices, state.search])

  const sorted = useMemo(() => {
    const key = DEVICE_SORT[state.order.column]
    if (!key) return filtered
    const sign = state.order.dir === 'asc' ? 1 : -1
    return [...filtered].sort((a, b) => {
      const left = key(a)
      const right = key(b)
      if (typeof left === 'number' && typeof right === 'number') return sign * (left - right)
      return sign * String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' })
    })
  }, [filtered, state.order.column, state.order.dir])

  const shown = sorted.slice(state.start, state.start + state.length)

  return (
    <div className="panel panel-default rd-org-devices" id="rustdesk-devices">
      <div className="panel-heading">
        <h3 className="panel-title">RustDesk on this organization's devices</h3>
      </div>
      <div className="panel-body">
        <DataTable
          id={DEVICES_TABLE}
          columns={columns}
          rows={shown as DeviceRow[]}
          total={sorted.length}
          fetching={false}
          state={state}
          onState={setState}
          rowId={(row) => `rd_device_${Number(row.deviceId)}`}
          emptyHint="No devices in this organization."
          footerButtons={{ copy: true }}
          exportName="RustDesk devices"
        />
      </div>
    </div>
  )
}
